import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Radio, ShieldPlus } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useSession } from "@/hooks/useSession";
import { api, type FPExplain, type SecEvent } from "@/lib/api";
import { fmtTime } from "@/lib/utils";

const ACTION_VARIANT: Record<string, "destructive" | "warning" | "success" | "secondary"> = {
  blocked: "destructive", challenged: "warning", detected: "warning",
  allowed: "success", error: "secondary", uninspected: "secondary",
};

const ACTION_FILTERS = ["", "blocked", "challenged", "detected", "allowed", "error", "uninspected"];

export default function Events() {
  const qc = useQueryClient();
  const { user } = useSession();
  const canExcept = user?.role === "owner" || user?.role === "operator";
  const [filter, setFilter] = useState("");
  const [live, setLive] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [excEvent, setExcEvent] = useState<SecEvent | null>(null);
  const [excForm, setExcForm] = useState({ reason: "", hours: "24", scope: "path" });
  const [excErr, setExcErr] = useState("");

  const { data } = useQuery({
    queryKey: ["events", filter],
    queryFn: () =>
      api.get<{ items: SecEvent[] }>(
        `/api/events?limit=200${filter ? `&action=${filter}` : ""}`,
      ).then((r) => r.items),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    const es = new EventSource("/api/stream");
    es.onopen = () => setLive(true);
    es.onerror = () => setLive(false);
    es.addEventListener("sec_event", () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    });
    return () => es.close();
  }, [qc]);

  // nx.falsepositive: explain + directive preview; 409 = module off → hide
  const explain = useQuery({
    queryKey: ["fp-explain", excEvent?.id],
    queryFn: () => api.get<FPExplain>(`/api/events/${excEvent!.id}/explain`)
      .catch(() => null),
    enabled: !!excEvent,
  });

  const fpScope = () => {
    const e = excEvent!;
    return excForm.scope === "path"
      ? { path: e.path.split("?")[0] }
      : excForm.scope === "arg" ? { arg: "q" } : {};
  };

  const createExc = useMutation({
    mutationFn: async () => {
      const e = excEvent!;
      const scope = fpScope();
      const body = {
        app_id: e.app_id, event_id: e.id, rule_id: e.rule_id,
        scope, reason: excForm.reason,
        expires_in_hours: Number(excForm.hours),
        confirm_broad: excForm.scope === "global",
      };
      try {
        // guided path (module on): scope enforced server-side
        return await api.post("/api/fp/proposals", body);
      } catch (err) {
        if (!String((err as Error).message).includes("not enabled")) throw err;
        // module off: legacy endpoint (same durable row + sync job)
        return await api.post("/api/exceptions", {
          app_id: e.app_id, event_id: e.id, rule_id: e.rule_id,
          control: "waf", scope, reason: excForm.reason,
          expires_in_hours: Number(excForm.hours),
        });
      }
    },
    onSuccess: () => {
      setExcEvent(null); setExcForm({ reason: "", hours: "24", scope: "path" });
      qc.invalidateQueries({ queryKey: ["exceptions"] });
    },
    onError: (e) => setExcErr(e.message),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            Security Events
            {live && (
              <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                <Radio className="h-3 w-3 animate-pulse-dot" /> live
              </span>
            )}
          </span>
        }
        description="Normalized from BunkerWeb instance metrics. Events marked uninspected were never evaluated by the WAF and are never counted as protected."
      >
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-44">
          {ACTION_FILTERS.map((f) => (
            <option key={f} value={f}>{f ? `${f} only` : "all actions"}</option>
          ))}
        </Select>
      </PageHeader>

      <Card>
        <Table>
          <THead>
            <TR>
              <TH className="w-6"></TH><TH>Time</TH><TH>Action</TH><TH>Control</TH>
              <TH>Client</TH><TH>Host</TH><TH>Request</TH><TH>Status</TH><TH>Rule</TH><TH></TH>
            </TR>
          </THead>
          <TBody>
            {(data ?? []).map((e) => {
              const open = openId === e.id;
              return (
                <Fragment key={e.id}>
                  <TR className="cursor-pointer" onClick={() => setOpenId(open ? null : e.id)}>
                    <TD className="w-6 px-2 text-muted-foreground">
                      {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </TD>
                    <TD className="whitespace-nowrap text-xs">{fmtTime(e.ts)}</TD>
                    <TD><Badge variant={ACTION_VARIANT[e.action] ?? "secondary"}>{e.action}</Badge></TD>
                    <TD className="text-xs">{e.control}</TD>
                    <TD className="font-mono text-xs">
                      {e.client_ip}{e.country ? <span className="text-muted-foreground"> ({e.country})</span> : ""}
                    </TD>
                    <TD className="max-w-[140px] truncate font-mono text-xs" title={e.host}>{e.host}</TD>
                    <TD className="max-w-[200px] truncate font-mono text-xs" title={e.path}>
                      <span className="text-muted-foreground">{e.method}</span> {e.path}
                    </TD>
                    <TD className="num text-xs">{e.status_code}</TD>
                    <TD className="font-mono text-xs">{e.rule_id || "—"}</TD>
                    <TD className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      {canExcept && e.action === "blocked" && e.rule_id && e.app_id && (
                        <Button size="sm" variant="ghost" title="Create WAF exception from this event"
                          onClick={() => setExcEvent(e)}>
                          <ShieldPlus className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TD>
                  </TR>
                  {open && (
                    <TR>
                      <TD colSpan={10} className="bg-muted/30 px-6 py-3">
                        <div className="grid gap-x-8 gap-y-1 font-mono text-xs sm:grid-cols-2 lg:grid-cols-3">
                          <div><span className="text-muted-foreground">service:</span> {e.service}</div>
                          <div><span className="text-muted-foreground">inspected:</span> {String(e.inspected)}</div>
                          <div><span className="text-muted-foreground">app:</span>{" "}
                            {e.app_id
                              ? <Link className="text-primary hover:underline" to={`/applications/${e.app_id}`}>{e.app_id.slice(0, 8)}</Link>
                              : "—"}
                          </div>
                          {Object.entries(e.meta ?? {}).map(([k, v]) => (
                            <div key={k} className="truncate" title={JSON.stringify(v)}>
                              <span className="text-muted-foreground">{k}:</span> {JSON.stringify(v)}
                            </div>
                          ))}
                        </div>
                      </TD>
                    </TR>
                  )}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
        {(data ?? []).length === 0 && (
          <EmptyState icon={Radio} title="No events yet"
            hint="Send traffic through the edge; the collector polls instance metrics every minute." />
        )}
      </Card>

      <Dialog open={!!excEvent} onClose={() => setExcEvent(null)}
        title="Create exception from event"
        description={excEvent ? `Rule ${excEvent.rule_id} on ${excEvent.host} — auto-expires, no permanent holes.` : ""}>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setExcErr(""); createExc.mutate(); }}>
          {explain.data && (
            <div className="rounded border border-border/60 bg-muted/30 px-3 py-2 text-xs space-y-1">
              <div><span className="text-muted-foreground">规则：</span>{explain.data.rule_name}</div>
              <div><span className="text-muted-foreground">命中：</span>{explain.data.rule_detail}
                {explain.data.matched_data && <span className="font-mono"> · {explain.data.matched_data}</span>}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <Select value={excForm.scope} onChange={(e) => setExcForm({ ...excForm, scope: e.target.value })}>
                <option value="path">this path only ({excEvent?.path.split("?")[0]})</option>
                <option value="global">whole service</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Expires in</Label>
              <Select value={excForm.hours} onChange={(e) => setExcForm({ ...excForm, hours: e.target.value })}>
                <option value="1">1 hour</option>
                <option value="24">24 hours</option>
                <option value="72">3 days</option>
                <option value="168">7 days</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Reason (audit trail, min 8 chars)</Label>
            <Input value={excForm.reason} onChange={(e) => setExcForm({ ...excForm, reason: e.target.value })}
              placeholder="False positive — legitimate search query" minLength={8} required />
          </div>
          {excErr && <p className="text-sm text-destructive">{excErr}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setExcEvent(null)}>Cancel</Button>
            <Button disabled={createExc.isPending}>{createExc.isPending ? "Creating…" : "Create exception"}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
